/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { ReleaseFilters, ReleaseResponse } from '@/client';
import { ContentContainer } from '@/components/ContentContainer';
import { PageHeader } from '@/components/PageHeader';
import { QueryWrapper } from '@/components/QueryWrapper';
import { useAllTenantReleasesQuery } from '@/hooks/query/useAllTenantReleasesQuery';
import { usePaginatedReleasesQuery } from '@/hooks/query/usePaginatedReleasesQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { ReleaseManagementTable } from '@/pages/ReleaseManagement/ReleaseManagementTable';
import { Container } from '@/primitives/Container';
import { Flex } from '@/primitives/Flex';
import { Switch } from '@/primitives/Switch';
import { Text } from '@/primitives/Text';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { UpdateParams } from '@/utils/params';

export function ReleaseManagementPage() {
  const env = window.mimicConfig.MIMIC_MGMT_PLANE_ENVIRONMENT || 'dev';
  const { t } = useMimicTranslation('releaseManagement');
  const tenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const releasesQuery = usePaginatedReleasesQuery({
    type: 'queryParams',
    params: {
      filters: {
        latestBuild: false,
      },
    },
  });

  const tenantReleasesQuery = useAllTenantReleasesQuery();

  if (!tenantID) return null;

  // Combine both queries for the QueryWrapper
  const combinedQuery = {
    data: releasesQuery.data,
    isLoading: releasesQuery.isLoading || tenantReleasesQuery.isLoading,
    isFetching: releasesQuery.isFetching || tenantReleasesQuery.isFetching,
    isError: releasesQuery.isError || tenantReleasesQuery.isError,
    error: releasesQuery.error || tenantReleasesQuery.error,
  };

  // Create a Set of published release IDs for quick lookup
  const getPublishedReleaseIds = (): Set<string> => {
    const publishedIds = new Set<string>();
    const allTenantReleases = tenantReleasesQuery.data;
    if (allTenantReleases) {
      allTenantReleases.forEach((release) => publishedIds.add(release.id));
    }
    return publishedIds;
  };

  const handleLatestBuildChange = (checked: boolean) => {
    const filters: Partial<ReleaseFilters> = { latestBuild: checked };
    releasesQuery.updateQueryParams({ filters, paginationParams: { number: 1 } });
  };

  const handleUpdateQueryParams: UpdateParams<ReleaseResponse, Partial<ReleaseFilters>> = (params) => {
    releasesQuery.updateQueryParams(params);
  };

  const latestBuildToggle =
    env !== 'prod' && env !== 'uat' ? (
      <Flex gap="xs" align="center">
        <Switch
          dataTestId="latest-build-filter"
          checked={releasesQuery.filters.latestBuild || false}
          onChange={handleLatestBuildChange}
          style={{ width: '40px' }}
        />
        <Text size="sm">{t('filters.latestBuild')}</Text>
      </Flex>
    ) : null;

  return (
    <ContentContainer width="100%">
      <Container full style={{ width: '100%' }}>
        <PageHeader title={t('listTitle')} level={2} />
        <Text type="subtle" style={{ marginBottom: '16px', display: 'block' }}>
          {t('description')}
        </Text>
        <QueryWrapper queryResult={combinedQuery}>
          {(releasesData) => (
            <ReleaseManagementTable
              releasesData={releasesData}
              publishedReleaseIds={getPublishedReleaseIds()}
              updateQueryParams={handleUpdateQueryParams}
              releaseFilters={releasesQuery.filters}
              tenantId={tenantID}
              primaryAction={latestBuildToggle}
            />
          )}
        </QueryWrapper>
      </Container>
    </ContentContainer>
  );
}
